import { cloneElement, Component } from 'react';
import { arrayOf, bool, element, func, number, object, oneOf, oneOfType, shape, string } from 'prop-types';
import { InputType, KeyCode, SensitiveData } from '@philips-internal/oswald-ui-constants';
import { ChangeEvent, FocusEvent } from '@philips-internal/oswald-ui-core/lib/component/types/index.js';
import { ModelService } from '@philips-internal/oswald-ui-core/lib/services/index.js';
import { enumValues, getLostProps, isFunction } from '@philips-internal/oswald-ui-utils';
import { hideSensitiveData } from '../../utils/hide-sensitive-data.js';
const SEPARATOR_REGEX = /\/|-|\.|\s/;
const NUMERIC_MASKS = [
    'currency',
    'float',
    'integer',
    'numeric',
];
const SELECTION_RANGE_NOT_SUPPORTED = [
    InputType.EMAIL,
    InputType.NUMBER,
];
class InputFormatter extends Component {
    /**
     * Prettifies a given value according to formatter.
     *
     * @param {object} properties - Properties to generate pretty value.
     * @param {boolean} [properties.focused] - Used to add options to a numeric formatter.
     * @param {Formatters | undefined} [properties.formatters] - Formatters used to format the value.
     * @param {string | number} [properties.value] - Value to be formatted.
     * @returns {string} pretty value.
     */
    static formatPrettyValue(params) {
        const { focused, formatter, value } = params;
        if (!formatter) {
            return String(value);
        }
        const options = { ...formatter.options };
        if (NUMERIC_MASKS.includes(formatter.name)) {
            if (focused) {
                options.padFractionalZeros = false;
            }
            else {
                options.normalizeZeros = true;
            }
        }
        return ModelService.format(formatter.name, value, options);
    }
    constructor(props) {
        super(props);
        // We assign `null` to the variable and set it to `null` later to ensure consistent behavior across different browsers when using the `setSelectionRange` method.
        // Chromium based browsers accept both `null` and `undefined` as empty values for the method, without triggering a focus change.
        // WebKit based browsers only accept `null` as an empty value, and if the method is called with any other value, it will also give focus to the element.
        this.cursorPos = null;
        this.inputRef = null;
        this.getPatternSeparators = (pattern) => {
            return pattern.split('').reduce((acc, char, index) => (SEPARATOR_REGEX.test(char) ? [...acc, index] : acc), []);
        };
        this.findNextCursorPosition = (target) => {
            const { formatter } = this.props;
            const { selectionStart } = target;
            const pattern = ModelService.getFormatterPattern(formatter.name);
            const separators = this.getPatternSeparators(pattern);
            const isAtSeparator = (idx) => separators.includes(idx);
            if (selectionStart && this.isBackspace && isAtSeparator(selectionStart)) {
                return selectionStart - 1;
            }
            if (selectionStart && !this.isBackspace && isAtSeparator(selectionStart - 1)) {
                return selectionStart + 1;
            }
            return selectionStart;
        };
        this.onKeyDown = (event) => {
            const { onKeyDown } = this.props;
            this.isBackspace = this.getKeyCode(event) === KeyCode.BACKSPACE;
            isFunction(onKeyDown) && onKeyDown(event);
        };
        this.onChange = (event) => {
            const { value: inputValue, selectionStart } = event.target;
            const { formatter, onChange, value: oldValue } = this.props;
            const { focused } = this.state;
            const isCursorAtEnd = inputValue.length === selectionStart;
            let finalValue = inputValue;
            if (!isCursorAtEnd && formatter.name) {
                this.cursorPos = this.findNextCursorPosition(event.target);
                // Erased a separator, so we also remove the char before it
                if (selectionStart && this.cursorPos && this.cursorPos < selectionStart) {
                    finalValue = this.removeCharAt(inputValue, this.cursorPos);
                }
            }
            const formattedValue = this.format(finalValue);
            const parsedValue = this.parse(formattedValue);
            isFunction(onChange) && onChange(new ChangeEvent({
                oldValue: String(oldValue),
                prettyValue: formattedValue,
                value: parsedValue,
            }));
            const newInputValue = this.format(finalValue, focused);
            this.setState({ inputValue: newInputValue });
            this.isBackspace = false;
        };
        this.onFocus = () => {
            const { onFocus, value } = this.props;
            const inputValue = this.format(value);
            this.setState({ focused: true, inputValue });
            isFunction(onFocus) && onFocus(new FocusEvent());
        };
        this.onBlur = () => {
            const { onBlur, value } = this.props;
            const inputValue = this.format(value);
            this.setState({ focused: false, inputValue });
            isFunction(onBlur) && onBlur(new FocusEvent());
        };
        this.format = (value, focused = false) => {
            const { formatter } = this.props;
            return InputFormatter.formatPrettyValue({ focused, formatter, value });
        };
        this.parse = (value) => {
            const { formatter } = this.props;
            return formatter.name ? ModelService.parse(formatter.name, value, formatter.options) : value;
        };
        const { value } = props;
        this.state = {
            focused: false,
            inputValue: this.format(value, false),
        };
    }
    componentDidUpdate(prevProps) {
        const { value } = this.props;
        if (prevProps.value !== value) {
            this.setState((currentState) => {
                const inputValue = this.format(value);
                if (inputValue === this.format(currentState.inputValue)) {
                    return currentState;
                }
                return { ...currentState, inputValue };
            });
        }
        if (this.cursorPos !== null) {
            this.restoreCursorPosition();
        }
    }
    focus() {
        return this.inputRef?.focus();
    }
    select() {
        return this.inputRef?.select();
    }
    removeCharAt(str, index) {
        return str.slice(0, index) + str.slice(index + 1);
    }
    restoreCursorPosition() {
        // Method setSelectionRange() is not supported in all input types
        // https://developer.mozilla.org/en-US/docs/Web/API/HTMLInputElement/setSelectionRange
        if (this.inputRef && SELECTION_RANGE_NOT_SUPPORTED.includes(this.inputRef.type)) {
            return;
        }
        this.inputRef?.setSelectionRange(this.cursorPos, this.cursorPos);
        this.cursorPos = null;
    }
    getKeyCode(event) {
        return Number.isInteger(event.which) ? event.which : event.keyCode;
    }
    render() {
        const { children, readOnly, sensitiveData } = this.props;
        const { inputValue } = this.state;
        const lostProps = getLostProps(InputFormatter.propTypes, this.props);
        const value = readOnly ? hideSensitiveData(inputValue, sensitiveData) : inputValue;
        return cloneElement(children, {
            ...lostProps,
            //@ts-ignore - ignoring because TS keeps messing with the children type
            onBlur: this.onBlur,
            onChange: this.onChange,
            onFocus: this.onFocus,
            onKeyDown: this.onKeyDown,
            ref: (el) => (this.inputRef = el),
            value,
        });
    }
}
InputFormatter.propTypes = {
    children: oneOfType([element, arrayOf(element)]),
    formatter: shape({ name: string, options: object }),
    onBlur: func,
    onChange: func,
    onFocus: func,
    onKeyDown: func,
    readOnly: bool,
    sensitiveData: oneOf(enumValues(SensitiveData)),
    value: oneOfType([number, string]),
};
InputFormatter.defaultProps = {
    formatter: {},
};
export { InputFormatter, };
//# sourceMappingURL=input-formatter.js.map